import React, { CSSProperties, ReactNode, useMemo } from "react";
import MultiItemSelect from "common/Filters/common/MultiItemSelect";
import { FilterSectionData } from "@shared/features/filters/types";
import pluralize from "pluralize";
import { getAllSelectedDisplay } from "@shared/features/filters/utils";
import {
  CheckboxFilterType,
  FilterSectionType,
  FilterType,
  LanguageNameSpaces,
} from "@shared/types";
import Text from "common/Typography/Text";
import useMeasure from "utils/hooks/useMeasure";
import Rate from "common/Rate/Rate";
import { useBottomNavigator } from "utils/hooks/misc";
import { MobileResetButton } from "common/Filters/common/styles";
import styled from "styled-components";
import Flex from "common/Boxes/Flex.tsx";
import Switch from "common/Buttons/Switch.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";

const MultiSelectFiltersSection = <T extends CheckboxFilterType>({
  filterType,
  title,
  onChange,
  value,
  options,
  displayData,
  isSingleRowView,
  isChipsView,
  getChipsText,
  filterSectionType,
  resetButtonData,
  toggleData,
}: FilterSectionData<T>) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const isSymbolsFilter = filterType === FilterType.SYMBOLS;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMultiple = filterSectionType !== FilterSectionType.RADIO;

  let allSelected = false;
  if (value?.length === options?.length) {
    allSelected = true;
  }

  const getPlaceholder = () => {
    if (isChipsView && isSingleRowView) return getChipsText();
    return allSelected
      ? getAllSelectedDisplay(title, isSingleRowView, true, t)
      : "";
  };

  let selectStyle: CSSProperties = {
    height: "32px",
    display: "flex",
    alignItems: "center",
  };
  if (isSingleRowView) {
    selectStyle = {
      ...selectStyle,
      minWidth: Math.max(80, width + 22),
      maxWidth: 200,
    };
  }

  const placeholder = isSingleRowView ? (
    <div ref={measureRef} style={{ width: "max-content" }}>
      <Text style={{ color: "inherit" }}>{getPlaceholder()}</Text>
    </div>
  ) : (
    getPlaceholder()
  );

  const getOptionDisplay = (option: string | number) => {
    let optionDisplay: string | number | ReactNode = option;
    if (filterType === FilterType.AVG_RATING) {
      if (option === -1) optionDisplay = t("filters.common.unrated");
      else {
        optionDisplay = (
          <Rate
            disabled
            defaultValue={option as number}
            style={{ fontSize: 13 }}
          />
        );
      }
    }
    return optionDisplay;
  };

  const toggle = toggleData?.show ? (
    <ToggleWrapper>
      <Switch
        checked={toggleData.isChecked}
        onClick={toggleData.onChange}
        size="small"
      />
      <ToggleText>{toggleData.text}</ToggleText>
    </ToggleWrapper>
  ) : undefined;

  const select = useMemo(() => {
    if (options && value) {
      return (
        <MultiItemSelect
          allItems={options as string[]}
          selectedItems={
            !isBottomNavigator && allSelected ? [] : (value as string[])
          }
          onChange={onChange}
          placeholder={placeholder}
          disabled={options.length === 0}
          getIconProps={displayData.getIconProps}
          getAccountId={displayData.getAccountId}
          style={selectStyle}
          maxTagPlaceholder={(numSelected) =>
            allSelected ? placeholder : pluralize(title, numSelected, true)
          }
          popupMatchSelectWidth={
            isSymbolsFilter && isSingleRowView && options.length > 20
              ? 300
              : false
          }
          renderOption={displayData.getDisplay ?? getOptionDisplay}
          isMultiple={isMultiple}
          isSingleRowView={isSingleRowView}
          width={width}
          title={title}
          allSelected={allSelected}
          bottomExtraElement={
            isBottomNavigator && isSingleRowView && isMultiple ? (
              <MobileResetButton onClick={resetButtonData.onClick}>
                {resetButtonData.resetText}
              </MobileResetButton>
            ) : undefined
          }
          popupRender={(menu) => (
            <>
              {toggle}
              {menu}
            </>
          )}
        />
      );
    }
    return null;
  }, [
    options,
    value,
    width,
    isSingleRowView,
    allSelected,
    toggleData,
    i18n.language,
  ]);

  const style: CSSProperties = {};
  if (filterType === FilterType.SYMBOLS && !isSingleRowView) {
    style.minWidth = 180;
  }
  if (!isChipsView) {
    style.marginBottom = 8;
  }

  return <div style={style}>{select}</div>;
};

export default MultiSelectFiltersSection;

const ToggleWrapper = styled(Flex)`
  justify-content: flex-start;
  margin: 8px 12px;
`;
const ToggleText = styled(Text)`
  margin-left: 8px;
`;
